import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./CurrencyPairManagementPage.css";

const mapPairFromApi = (p) => ({
  id: p._id,
  pairName: `${p.base_currency || ""}/${p.quote_currency || ""}`,
  base_currency: p.base_currency ?? "",
  quote_currency: p.quote_currency ?? "",
  sellPrice: p.sell_price ?? "",
  buyPrice: p.buy_price ?? "",
  availability: p.available || "GLOBAL",
  status: p.status || "Active",
  maker_fee: p.maker_fee ?? "",
  taker_fee: p.taker_fee ?? "",
  min_order_qty: p.min_order_qty ?? "",
  max_order_qty: p.max_order_qty ?? "",
  step_size: p.step_size ?? "",
  tick_size: p.tick_size ?? "",
  min_notional: p.min_notional ?? "",
  base_asset_precision: p.base_asset_precision ?? "",
  quote_asset_precision: p.quote_asset_precision ?? "",
  price_source: p.price_source ?? "",
  cmc_id: p.cmc_id ?? "",
  icon_path: p.icon_path ?? "",
  pairType: typeof p.pairType === "boolean" ? p.pairType : p.pairType ?? false,
});

const CurrencyPairManagementPage = () => {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [searchPair, setSearchPair] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [exportOpen, setExportOpen] = useState(false);
  const [listModal, setListModal] = useState(null);
  const [statusActionLoading, setStatusActionLoading] = useState(false);
  const [viewPairId, setViewPairId] = useState(null);
  const [editPairId, setEditPairId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editForm, setEditForm] = useState({
    availability: "GLOBAL",
    status: "Active",
    buy_price: "",
    sell_price: "",
    maker_fee: "",
    taker_fee: "",
    min_order_qty: "",
    max_order_qty: "",
    step_size: "",
    tick_size: "",
    min_notional: "",
    base_asset_precision: "",
    quote_asset_precision: "",
    price_source: "",
    cmc_id: "",
    icon_path: "",
    pairType: false,
  });

  const fetchPairs = useCallback(async () => {
    setLoading(true);
    setListError("");
    const params = statusFilter ? { status: statusFilter } : {};
    const res = await AdminService.getPairsList(params);
    setLoading(false);
    if (res?.success && Array.isArray(res.data)) {
      setPairs(res.data.map(mapPairFromApi));
    } else {
      setListError(res?.message || "Failed to load pairs.");
      setPairs([]);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchPairs();
  }, [fetchPairs]);

  const filteredPairs = pairs.filter((p) =>
    p.pairName.toLowerCase().includes(searchPair.toLowerCase())
  );
  const totalRows = filteredPairs.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const pageStart = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const pageEnd = Math.min(currentPage * rowsPerPage, totalRows);
  const paginatedPairs = filteredPairs.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handleToggleStatus = async (pairId) => {
    const pair = pairs.find((p) => p.id === pairId);
    if (!pair) return;
    const newStatus = pair.status === "Active" ? "Inactive" : "Active";
    setStatusActionLoading(true);
    const res = await AdminService.updatePairStatus(pairId, newStatus);
    setStatusActionLoading(false);
    if (res?.success) {
      setPairs((prev) =>
        prev.map((p) => (p.id === pairId ? { ...p, status: newStatus } : p))
      );
      setListModal(null);
    } else {
      setListError(res?.message || "Failed to update pair status.");
    }
  };

  const openViewModal = (pairId) => {
    setViewPairId(pairId);
  };

  const openEditModal = (pairId) => {
    const pair = pairs.find((p) => p.id === pairId);
    if (!pair) return;
    setEditError("");
    setEditPairId(pairId);
    setEditForm({
      status: pair.status ?? "Active",
      availability: pair.availability ?? "GLOBAL",
      buy_price: pair.buyPrice ?? "",
      sell_price: pair.sellPrice ?? "",
      maker_fee: pair.maker_fee ?? "",
      taker_fee: pair.taker_fee ?? "",
      min_order_qty: pair.min_order_qty ?? "",
      max_order_qty: pair.max_order_qty ?? "",
      step_size: pair.step_size ?? "",
      tick_size: pair.tick_size ?? "",
      min_notional: pair.min_notional ?? "",
      base_asset_precision: pair.base_asset_precision ?? "",
      quote_asset_precision: pair.quote_asset_precision ?? "",
      price_source:
        pair.price_source ?? (pair.availability === "GLOBAL" ? "BINANCE" : "CMC"),
      cmc_id:
        (pair.price_source ?? (pair.availability === "GLOBAL" ? "BINANCE" : "CMC")) === "CMC"
          ? pair.cmc_id ?? ""
          : "",
      icon_path: pair.icon_path ?? "",
      pairType: Boolean(pair.pairType),
    });
  };

  const closeEditModal = () => {
    setEditPairId(null);
    setEditLoading(false);
    setEditError("");
  };

  const closeViewModal = () => {
    setViewPairId(null);
  };

  const toMaybeNumber = (v) => {
    if (v === "" || v === null || v === undefined) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const handleSubmitEdit = async () => {
    const pair = pairs.find((p) => p.id === editPairId);
    if (!pair) return;

    setEditError("");
    setEditLoading(true);
    try {
      if (editForm.price_source === "CMC" && !String(editForm.cmc_id || "").trim()) {
        setEditError("cmc_id is required when price_source is CMC.");
        return;
      }

      const payload = {
        status: editForm.status,
        available: editForm.availability,
        ...(toMaybeNumber(editForm.buy_price) !== undefined && { buy_price: toMaybeNumber(editForm.buy_price) }),
        ...(toMaybeNumber(editForm.sell_price) !== undefined && { sell_price: toMaybeNumber(editForm.sell_price) }),
        ...(toMaybeNumber(editForm.maker_fee) !== undefined && { maker_fee: toMaybeNumber(editForm.maker_fee) }),
        ...(toMaybeNumber(editForm.taker_fee) !== undefined && { taker_fee: toMaybeNumber(editForm.taker_fee) }),
        ...(toMaybeNumber(editForm.min_order_qty) !== undefined && { min_order_qty: toMaybeNumber(editForm.min_order_qty) }),
        ...(toMaybeNumber(editForm.max_order_qty) !== undefined && { max_order_qty: toMaybeNumber(editForm.max_order_qty) }),
        ...(toMaybeNumber(editForm.step_size) !== undefined && { step_size: toMaybeNumber(editForm.step_size) }),
        ...(toMaybeNumber(editForm.tick_size) !== undefined && { tick_size: toMaybeNumber(editForm.tick_size) }),
        ...(toMaybeNumber(editForm.min_notional) !== undefined && { min_notional: toMaybeNumber(editForm.min_notional) }),
        ...(toMaybeNumber(editForm.base_asset_precision) !== undefined && { base_asset_precision: toMaybeNumber(editForm.base_asset_precision) }),
        ...(toMaybeNumber(editForm.quote_asset_precision) !== undefined && { quote_asset_precision: toMaybeNumber(editForm.quote_asset_precision) }),
        ...(editForm.price_source ? { price_source: editForm.price_source } : {}),
        ...(String(editForm.cmc_id || "").trim() ? { cmc_id: toMaybeNumber(editForm.cmc_id) ?? editForm.cmc_id } : {}),
        ...(editForm.icon_path ? { icon_path: editForm.icon_path } : {}),
      };

      const res = await AdminService.updateSpotPair(editPairId, payload);
      if (res?.success) {
        closeEditModal();
        await fetchPairs();
      } else {
        setEditError(res?.message || "Failed to update pair.");
      }
    } catch (err) {
      setEditError("Failed to update pair.");
      console.error(err);
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage currency-pair-page">
      <section className="dashboard-hero currency-pair-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Currency Pair Management</span>
          </p>
          <h1 className="dashboard-hero__title">Currency Pair Management</h1>
          <p className="dashboard-hero__subtitle">View and manage trading pairs.</p>
        </div>
        <div className="currency-pair-hero__actions">
          <Link to="/dashboard/addCurrencyPair" className="cp-btn cp-btn--primary cp-btn--add">
            <i className="fas fa-plus" /> Add Currency Pair
          </Link>
        </div>
        <div className="dashboard-hero__icon currency-pair-hero__icon">
          <i className="fas fa-columns" />
        </div>
      </section>

      <div className="dashboard-body currency-pair-body">
        <div className="currency-pair-card currency-pair-card--table-only">
          <div className="currency-pair-table-block">
            <div className="currency-pair-table-head">
              <div className="currency-pair-section-head">
                <i className="fas fa-list" />
                <h2>Currency Pair Details ({pairs.length})</h2>
              </div>
              <div className="cp-table-actions">
                <div className="cp-filter-wrap">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="cp-filter-select"
                  >
                    <option value="">All status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                <div className="cp-export-wrap">
                  <button type="button" className="cp-btn cp-btn--export" onClick={() => setExportOpen((v) => !v)}>
                    <i className="fas fa-download" /> Export <i className="fas fa-chevron-down" />
                  </button>
                  {exportOpen && (
                    <div className="cp-export-dropdown">
                      <button type="button">CSV</button>
                      <button type="button">Excel</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {listError && (
              <div className="cp-list-error">
                <i className="fas fa-exclamation-triangle" /> {listError}
                <button type="button" className="cp-btn cp-btn--outline cp-btn--sm" onClick={fetchPairs}>Retry</button>
              </div>
            )}

            {loading ? (
              <div className="cp-loading">
                <i className="fas fa-spinner fa-spin" /> Loading pairs…
              </div>
            ) : (
              <>
                <div className="cp-search-wrap">
                  <i className="fas fa-search" />
                  <input
                    type="text"
                    placeholder="Search here..."
                    value={searchPair}
                    onChange={(e) => setSearchPair(e.target.value)}
                    className="cp-search-input"
                  />
                </div>
                <div className="cp-table-wrap">
                  <table className="cp-table">
                    <thead>
                      <tr>
                        <th>Pair Name</th>
                        <th>Sell Price</th>
                        <th>Buy Price</th>
                        <th>Availability</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPairs.map((row) => (
                        <tr key={row.id}>
                          <td><strong>{row.pairName}</strong></td>
                          <td>{row.sellPrice}</td>
                          <td>{row.buyPrice}</td>
                          <td>
                            <span className={`cp-badge cp-badge--${row.availability === "GLOBAL" ? "global" : "local"}`}>
                              <i className="fas fa-globe" /> {row.availability}
                            </span>
                          </td>
                          <td>
                            <span className={`cp-badge cp-badge--${row.status === "Active" ? "global" : "local"}`}>
                              {row.status}
                            </span>
                          </td>
                          <td>
                            <div className="cp-action-btns">
                              <button
                                type="button"
                                className="cp-action-btn cp-action-btn--list"
                                onClick={() => openViewModal(row.id)}
                                title="View pair details"
                                disabled={statusActionLoading}
                              >
                                <i className="fas fa-eye" />
                                View
                              </button>
                              <button
                                type="button"
                                className="cp-action-btn cp-action-btn--delist"
                                onClick={() => openEditModal(row.id)}
                                title="Edit pair"
                                disabled={statusActionLoading}
                              >
                                <i className="fas fa-edit" />
                                Edit
                              </button>
                              <button
                                type="button"
                                className={`cp-action-btn cp-action-btn--list ${row.status === "Active" ? "" : "cp-action-btn--delist"}`}
                                onClick={() => setListModal(row.id)}
                                title={row.status === "Active" ? "Delist" : "List"}
                                disabled={statusActionLoading}
                              >
                                <i className={`fas ${row.status === "Active" ? "fa-times" : "fa-check"}`} />
                                {row.status === "Active" ? "Delist" : "List"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="cp-pagination">
                  <div className="cp-pagination-rows">
                    <span>Rows per page:</span>
                    <select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={500}>500</option>
                      <option value={1000}>1000</option>
                    </select>
                  </div>
                  <div className="cp-pagination-info">
                    {pageStart}-{pageEnd} of {totalRows}
                  </div>
                  <div className="cp-pagination-nav">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      className="cp-pagination-btn"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      <i className="fas fa-chevron-left" />
                    </button>
                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      className="cp-pagination-btn"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    >
                      <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {listModal && (() => {
        const pair = pairs.find((p) => p.id === listModal);
        if (!pair) return null;
        const isCurrentlyActive = pair.status === "Active";
        return (
          <div className="cp-modal-overlay" onClick={() => setListModal(null)}>
            <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{isCurrentlyActive ? "Delist Pair" : "List Pair"}</h3>
              <p>
                {isCurrentlyActive
                  ? `Delist ${pair.pairName}? The pair will be set to Inactive.`
                  : `List ${pair.pairName}? The pair will be set to Active.`}
              </p>
              <div className="cp-modal-actions">
                <button type="button" className="cp-btn cp-btn--outline" onClick={() => setListModal(null)}>Cancel</button>
                <button
                  type="button"
                  className="cp-btn cp-btn--primary"
                  onClick={() => handleToggleStatus(listModal)}
                  disabled={statusActionLoading}
                >
                  {statusActionLoading ? <><i className="fas fa-spinner fa-spin" /> Updating…</> : <><i className="fas fa-check" /> Confirm</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {viewPairId && (() => {
        const pair = pairs.find((p) => p.id === viewPairId);
        if (!pair) return null;
        return (
          <div className="cp-modal-overlay" onClick={closeViewModal}>
            <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
              <h3>View Pair</h3>
              <p>
                <strong>{pair.pairName}</strong>
              </p>
              <div className="addpair-summary">
                <div className="addpair-summary-row">
                  <span>Status</span>
                  <strong>{pair.status}</strong>
                </div>
                <div className="addpair-summary-row">
                  <span>Availability</span>
                  <strong>{pair.availability}</strong>
                </div>
                <div className="addpair-summary-row">
                  <span>Buy / Sell</span>
                  <strong>
                    {pair.buyPrice || "—"} / {pair.sellPrice || "—"}
                  </strong>
                </div>
                <div className="addpair-summary-row">
                  <span>Maker / Taker Fee</span>
                  <strong>
                    {pair.maker_fee || "—"} / {pair.taker_fee || "—"}
                  </strong>
                </div>
                <div className="addpair-summary-row">
                  <span>Min / Max Qty</span>
                  <strong>
                    {pair.min_order_qty || "—"} / {pair.max_order_qty || "—"}
                  </strong>
                </div>
                <div className="addpair-summary-row">
                  <span>Step / Tick</span>
                  <strong>
                    {pair.step_size || "—"} / {pair.tick_size || "—"}
                  </strong>
                </div>
                <div className="addpair-summary-row">
                  <span>Min Notional</span>
                  <strong>{pair.min_notional || "—"}</strong>
                </div>
              </div>
              <div className="cp-modal-actions">
                <button type="button" className="cp-btn cp-btn--primary" onClick={closeViewModal}>
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {editPairId && (() => {
        const pair = pairs.find((p) => p.id === editPairId);
        if (!pair) return null;
        return (
          <div className="cp-modal-overlay" onClick={closeEditModal}>
            <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Edit Pair</h3>
              {editError && (
                <p style={{ color: "#dc2626", marginTop: "-0.25rem", marginBottom: "0.75rem" }}>
                  {editError}
                </p>
              )}

              <div className="addpair-config-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Status</label>
                  <div className="cp-input-wrap">
                    <select value={editForm.status} onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Availability</label>
                  <div className="cp-input-wrap">
                    <select
                      value={editForm.availability}
                      onChange={(e) => {
                        const nextAvailability = e.target.value;
                        setEditForm((f) => {
                          if (nextAvailability === "GLOBAL") {
                            return { ...f, availability: "GLOBAL", price_source: "BINANCE", cmc_id: "" };
                          }
                          // LOCAL defaults to CMC
                          const nextPriceSource = "CMC";
                          return {
                            ...f,
                            availability: "LOCAL",
                            price_source: nextPriceSource,
                            cmc_id: nextPriceSource === "CMC" ? f.cmc_id : "",
                          };
                        });
                      }}
                    >
                      <option value="GLOBAL">GLOBAL</option>
                      <option value="LOCAL">LOCAL</option>
                    </select>
                  </div>
                </div>

                {editForm.availability === "GLOBAL" ? (
                  <div className="cp-field" style={{ marginBottom: 0 }}>
                    <label>Price Source</label>
                    <div className="cp-input-wrap">
                      <select value="BINANCE" disabled>
                        <option value="BINANCE">BINANCE</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="cp-field" style={{ marginBottom: 0 }}>
                    <label>Price Source</label>
                    <div className="cp-input-wrap">
                      <select
                        value={editForm.price_source || "CMC"}
                        onChange={(e) =>
                          setEditForm((f) => ({
                            ...f,
                            price_source: e.target.value,
                            cmc_id: e.target.value === "CMC" ? f.cmc_id : "",
                          }))
                        }
                      >
                        <option value="LOCAL">LOCAL (Manual)</option>
                        <option value="CMC">CMC (CoinMarketCap)</option>
                      </select>
                    </div>
                  </div>
                )}

                {editForm.price_source === "CMC" && (
                  <div className="cp-field" style={{ marginBottom: 0 }}>
                    <label>CMC ID</label>
                    <div className="cp-input-wrap">
                      <input
                        type="text"
                        value={editForm.cmc_id}
                        onChange={(e) => setEditForm((f) => ({ ...f, cmc_id: e.target.value }))}
                        placeholder="Enter CoinMarketCap ID"
                      />
                    </div>
                  </div>
                )}

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Icon Path</label>
                  <div className="cp-input-wrap">
                    <input
                      type="text"
                      value={editForm.icon_path}
                      onChange={(e) => setEditForm((f) => ({ ...f, icon_path: e.target.value }))}
                      placeholder="icons/..."
                    />
                  </div>
                </div>

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Buy Price</label>
                  <div className="cp-input-wrap">
                    <input
                      type="number"
                      step="any"
                      value={editForm.buy_price}
                      onChange={(e) => setEditForm((f) => ({ ...f, buy_price: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Sell Price</label>
                  <div className="cp-input-wrap">
                    <input
                      type="number"
                      step="any"
                      value={editForm.sell_price}
                      onChange={(e) => setEditForm((f) => ({ ...f, sell_price: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Maker Fee</label>
                  <div className="cp-input-wrap">
                    <input
                      type="number"
                      step="any"
                      value={editForm.maker_fee}
                      onChange={(e) => setEditForm((f) => ({ ...f, maker_fee: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Taker Fee</label>
                  <div className="cp-input-wrap">
                    <input
                      type="number"
                      step="any"
                      value={editForm.taker_fee}
                      onChange={(e) => setEditForm((f) => ({ ...f, taker_fee: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Min Order Qty</label>
                  <div className="cp-input-wrap">
                    <input
                      type="number"
                      step="any"
                      value={editForm.min_order_qty}
                      onChange={(e) => setEditForm((f) => ({ ...f, min_order_qty: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Max Order Qty</label>
                  <div className="cp-input-wrap">
                    <input
                      type="number"
                      step="any"
                      value={editForm.max_order_qty}
                      onChange={(e) => setEditForm((f) => ({ ...f, max_order_qty: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Step Size</label>
                  <div className="cp-input-wrap">
                    <input
                      type="number"
                      step="any"
                      value={editForm.step_size}
                      onChange={(e) => setEditForm((f) => ({ ...f, step_size: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Tick Size</label>
                  <div className="cp-input-wrap">
                    <input
                      type="number"
                      step="any"
                      value={editForm.tick_size}
                      onChange={(e) => setEditForm((f) => ({ ...f, tick_size: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Min Notional</label>
                  <div className="cp-input-wrap">
                    <input
                      type="number"
                      step="any"
                      value={editForm.min_notional}
                      onChange={(e) => setEditForm((f) => ({ ...f, min_notional: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Base Precision</label>
                  <div className="cp-input-wrap">
                    <input
                      type="number"
                      value={editForm.base_asset_precision}
                      onChange={(e) => setEditForm((f) => ({ ...f, base_asset_precision: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="cp-field" style={{ marginBottom: 0 }}>
                  <label>Quote Precision</label>
                  <div className="cp-input-wrap">
                    <input
                      type="number"
                      value={editForm.quote_asset_precision}
                      onChange={(e) => setEditForm((f) => ({ ...f, quote_asset_precision: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              <div className="cp-modal-actions">
                <button type="button" className="cp-btn cp-btn--outline" onClick={closeEditModal} disabled={editLoading}>
                  Cancel
                </button>
                <button type="button" className="cp-btn cp-btn--primary" onClick={handleSubmitEdit} disabled={editLoading}>
                  {editLoading ? <><i className="fas fa-spinner fa-spin" /> Updating…</> : "Update Pair"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </main>
  );
};

export default CurrencyPairManagementPage;
